import { useQuery } from '@tanstack/react-query'
import { createContext, createElement, type ReactNode, useCallback, useContext, useMemo } from 'react'

import type { ESKResponse, GetSidekicksResponse } from '@/services/api/api.types'

import { useGlobalState } from './useGlobalState'

export type ESKWithId = ESKResponse & { eskId: string }

export type AllEsksValue = {
  allEsks: ESKWithId[]
  isReady: boolean
  isLoading: boolean
  isFetching: boolean
  getEskName: (eskId: string | null | undefined) => string | null
  getEsk: (eskId: string | null | undefined) => ESKWithId | undefined
  sidekicksQuery: ReturnType<typeof useQuery<GetSidekicksResponse, Error>>
  sidekickPoolQuery: ReturnType<typeof useQuery<GetSidekicksResponse | null, Error>>
}

const AllEsksContext = createContext<AllEsksValue | undefined>(undefined)

/**
 * Internal hook that runs the sidekick queries and derived state.
 * Used only by AllEsksProvider so there is a single subscription.
 */
function useAllEsksQueries(): AllEsksValue {
  const { api } = useGlobalState()

  const sidekicksQuery = useQuery({
    queryKey: ['sponsor', 'sidekicks'],
    queryFn: api.getSidekicks,
    initialData: {
      mskTypes: [],
      eskTypes: [],
    },
    refetchOnWindowFocus: false,
  })

  const sidekickPoolQuery = useQuery({
    queryKey: ['sponsor', 'sidekick-pool'],
    queryFn: api.getSidekickPool,
    initialData: null,
    refetchOnWindowFocus: false,
  })

  const isReady = !sidekicksQuery.isFetching && !sidekickPoolQuery.isFetching

  const allEsks = useMemo((): ESKWithId[] => {
    const sponsorEsks = sidekicksQuery.data?.eskTypes ?? []
    const poolEsks = sidekickPoolQuery.data?.eskTypes ?? []

    const sponsorEskTypes = new Set(sponsorEsks.map(esk => esk.type))

    const sponsorEskOptions: ESKWithId[] = sponsorEsks.map(esk => ({
      ...esk,
      eskId: esk.type,
    }))

    const poolEskOptions: ESKWithId[] = poolEsks
      .filter(esk => !sponsorEskTypes.has(esk.type))
      .map(esk => ({
        ...esk,
        eskId: esk.type,
      }))

    const combined = [...sponsorEskOptions, ...poolEskOptions]
    const uniqueByType = new Map<string, ESKWithId>()
    for (const esk of combined) {
      if (uniqueByType.has(esk.eskId)) {
        console.warn(`[ESK] Duplicate ESK detected: ${esk.eskId} (${esk.name})`)
      } else {
        uniqueByType.set(esk.eskId, esk)
      }
    }
    return Array.from(uniqueByType.values())
  }, [sidekicksQuery.data, sidekickPoolQuery.data])

  const getEskName = useCallback(
    (eskId: string | null | undefined): string | null => {
      if (!eskId) return null
      if (eskId === 'PSK') return 'Personal Sidekick'
      if (!isReady) return null
      return allEsks.find(e => e.eskId === eskId)?.name ?? null
    },
    [allEsks, isReady],
  )

  const getEsk = useCallback(
    (eskId: string | null | undefined): ESKWithId | undefined => {
      if (!eskId) return undefined
      return allEsks.find(e => e.eskId === eskId)
    },
    [allEsks],
  )

  return {
    allEsks,
    isReady,
    isLoading: sidekicksQuery.isLoading || sidekickPoolQuery.isLoading,
    isFetching: sidekicksQuery.isFetching || sidekickPoolQuery.isFetching,
    getEskName,
    getEsk,
    sidekicksQuery,
    sidekickPoolQuery,
  }
}

export function AllEsksProvider(props: { children: ReactNode }) {
  const value = useAllEsksQueries()
  return createElement(AllEsksContext.Provider, { value }, props.children)
}

/**
 * Hook that fetches and combines ESKs from both sponsor and pool sources.
 * Must be used within AllEsksProvider (e.g. under DashboardLayout).
 * Provides proper loading state tracking to prevent showing raw IDs before data is ready.
 */
export function useAllEsks(): AllEsksValue {
  const value = useContext(AllEsksContext)
  if (value === undefined) {
    throw new Error('useAllEsks must be used within AllEsksProvider')
  }
  return value
}
